import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiDialogModule } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import {
  ActionAuthorizationService,
  doublonsBusterActionCodes,
} from '../../../../../../core/auth/action-authorization.service';
import { DoublonsBusterApiService } from '../../../../../../core/services/doublons-buster-api.service';

export type InvestorRelationDeletionDialogData = {
  investorId: number;
  personId: number;
  personLabel: string;
  relationId: number;
  relatedPersonId?: number;
  relationKind: 'famille' | 'autre' | 'couple';
  relationTypeLabel: string;
  relatedEntityLabel: string;
};

@Component({
  selector: 'dbw-investor-relation-deletion-dialog',
  imports: [OuiButtonModule, OuiDialogModule],
  templateUrl: './investor-relation-deletion-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './investor-relation-deletion-dialog.component.scss',
})
export class InvestorRelationDeletionDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<InvestorRelationDeletionDialogComponent>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly apiService = inject(DoublonsBusterApiService);
  private readonly snackbarService = inject(OuiSnackbarService);
  private readonly actionAuthorizationService = inject(ActionAuthorizationService);
  protected readonly data = inject<InvestorRelationDeletionDialogData>(MAT_DIALOG_DATA);

  protected readonly executing = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly canDelete = computed(
    () =>
      this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.investorRelationDeletionExecute) &&
      !this.executing()
  );

  protected close(): void {
    this.dialogRef.close({ relationDeleted: false });
  }

  protected deleteRelation(): void {
    if (!this.canDelete()) {
      return;
    }

    this.executing.set(true);
    this.error.set(null);

    const request$ =
      this.data.relationKind === 'couple'
        ? this.apiService.deleteInvestorCoupleRelation(
            this.data.investorId,
            this.data.personId,
            this.data.relationId,
            this.data.relatedPersonId!
          )
        : this.apiService.deleteInvestorRelation(
            this.data.investorId,
            this.data.personId,
            this.data.relationKind,
            this.data.relationId
          );

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.executing.set(false);
          this.snackbarService.open(result.message, 'success');
          this.dialogRef.close({
            relationDeleted: true,
            rolledBack: result.rolledBack,
            message: result.message,
          });
        },
        error: error => {
          this.executing.set(false);
          this.error.set(error?.error?.detail ?? 'Impossible de supprimer la relation.');
        },
      });
  }
}
